import { Button, Typography } from "@suraasa/placebo-ui"
import {
  Question,
  QuestionStatus,
  QuestionType,
  UserResponse,
} from "api/resources/assessments/types"
import FillBlanks from "features/Assessments/Attempt/Question/FillBlanks"
import { useState } from "react"

/**
 * Manual test bench for the fill-in-the-blanks component.
 * Route: /misc/fill-blanks-test
 *
 * Each card owns its response the same way the real attempt screen does
 * (including the "all null becomes []" normalisation done by markAnswer),
 * so the round trip through `question.response` is exercised too.
 */

const makeQuestion = (
  id: string,
  question: string,
  options: string[],
  response: UserResponse | null = null
): Question => ({
  id,
  questionType: QuestionType.FILL_IN_THE_BLANKS,
  options: Object.fromEntries(options.map((value, index) => [index, value])),
  question,
  status: QuestionStatus.UNATTEMPTED,
  questionNumber: 1,
  response,
  tempAssessmentPerformanceId: `temp-${id}`,
})

const testCases: { title: string; note: string; question: Question }[] = [
  {
    title: "Single blank",
    note: "The original bug: pick an option, then pick another. It should swap instantly.",
    question: makeQuestion("single", "The capital of France is __blank__.", [
      "Paris",
      "Berlin",
      "Madrid",
      "Rome",
    ]),
  },
  {
    title: "Two blanks",
    note: "Fill both, then tap either blank and pick a different option to replace it.",
    question: makeQuestion(
      "double",
      "Water is made of __blank__ and __blank__.",
      ["Hydrogen", "Oxygen", "Nitrogen", "Carbon"]
    ),
  },
  {
    title: "Three blanks, blank at the very start",
    note: "Checks segment rendering when the text starts and ends with a blank.",
    question: makeQuestion(
      "triple",
      "__blank__ comes before __blank__ which comes before __blank__",
      ["Morning", "Afternoon", "Evening", "Night"]
    ),
  },
  {
    title: "Pre-filled response",
    note: "Loads with blank 1 already answered. Active blank should start on blank 2.",
    question: makeQuestion(
      "prefilled",
      "A __blank__ has four sides and a __blank__ has three.",
      ["Square", "Triangle", "Circle"],
      [0, null]
    ),
  },
  {
    title: "Long options and multi-line text",
    note: "Checks wrapping of the blanks inside flowing paragraph text.",
    question: makeQuestion(
      "long",
      "In pedagogy, __blank__ refers to assessment that happens during learning, while __blank__ refers to assessment at the end of a unit.\nBoth are needed for a complete picture.",
      [
        "Formative assessment carried out continuously through the unit",
        "Summative assessment carried out at the close of the unit",
        "Diagnostic assessment carried out before teaching begins",
      ]
    ),
  },
  {
    title: "More blanks than options",
    note: "Edge case: 3 blanks, 2 options. The third blank can never be filled.",
    question: makeQuestion(
      "overflow",
      "__blank__, __blank__ and __blank__ are primary colours.",
      ["Red", "Blue"]
    ),
  },
]

const TestCase = ({
  title,
  note,
  question: initialQuestion,
}: (typeof testCases)[number]) => {
  const [response, setResponse] = useState<UserResponse | null>(
    initialQuestion.response
  )
  // Bumped on reset to remount the component, since it only re-reads
  // `question.response` when the question id changes.
  const [resetCount, setResetCount] = useState(0)

  const question: Question = { ...initialQuestion, response }

  return (
    <div className="mb-3 rounded-2xl border border-onSurface-200 p-2">
      <Typography variant="title4">{title}</Typography>
      <Typography variant="smallBody" className="mb-2 text-onSurface-500">
        {note}
      </Typography>

      <FillBlanks
        key={resetCount}
        question={question}
        onChange={(_, newResponse) => {
          // Mirrors markAnswer in useAssessment.ts
          setResponse(
            newResponse.every(element => element == null) ? [] : newResponse
          )
        }}
      />

      <div className="flex items-center gap-2">
        <Typography variant="strongSmallBody" className="text-onSurface-600">
          Response sent to parent: {JSON.stringify(response)}
        </Typography>
        <Button
          size="xs"
          variant="text"
          onClick={() => {
            setResponse(initialQuestion.response)
            setResetCount(count => count + 1)
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  )
}

const FillBlanksTest = () => (
  <div className="mx-auto max-w-[800px] p-3">
    <Typography variant="title2" className="mb-1">
      Fill in the blanks — manual test
    </Typography>
    <Typography variant="smallBody" className="mb-3 text-onSurface-500">
      The highlighted blank is the active one. Tapping an option fills it,
      replacing whatever was there. Remove an answer with the × on the blank or
      by tapping the option again.
    </Typography>

    {testCases.map(testCase => (
      <TestCase key={testCase.question.id} {...testCase} />
    ))}
  </div>
)

export default FillBlanksTest
